import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../context/AppDataContext';
import UserService from '../../services/userService';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import CustomerPrimaryButton from './CustomerPrimaryButton';
import { DEFAULT_PHONE_COUNTRY_CODE, toPhoneDto } from '../../utils/phoneUtils';

const resolvePhoneFields = (phone) => ({
  countryCode: phone?.countryCode || phone?.CountryCode || DEFAULT_PHONE_COUNTRY_CODE,
  number: phone?.number || phone?.Number || '',
});

const CustomerProfilePhoneForm = () => {
  const { t } = useTranslation();
  const { userPreferences, preferencesLoaded, updateUserPhone } = useAppData();
  const savedPhone = resolvePhoneFields(userPreferences?.phone);

  const [phoneCountryCode, setPhoneCountryCode] = useState(savedPhone.countryCode);
  const [phoneNumber, setPhoneNumber] = useState(savedPhone.number);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!preferencesLoaded) {
      return;
    }

    const next = resolvePhoneFields(userPreferences?.phone);
    setPhoneCountryCode(next.countryCode);
    setPhoneNumber(next.number);
  }, [preferencesLoaded, userPreferences?.phone]);

  const isUnchanged = phoneCountryCode === savedPhone.countryCode
    && phoneNumber.trim() === savedPhone.number;

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const phone = toPhoneDto(phoneCountryCode, phoneNumber);
      await UserService.updatePhone(phone);
      updateUserPhone?.(phone);
      setSuccess(t('customer.profile.phone.saveSuccess'));
    } catch (err) {
      console.error('Error updating phone number:', err);
      const errorCode = err.response?.data?.code;
      setError(errorCode === 'InvalidPhoneFormat'
        ? t('customer.profile.phone.invalid')
        : (err.response?.data?.message || t('customer.profile.phone.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <SalovarePhoneInput
        label={t('customer.profile.phone.label')}
        countryCode={phoneCountryCode}
        number={phoneNumber}
        onCountryCodeChange={(value) => {
          setPhoneCountryCode(value);
          setError(null);
          setSuccess(null);
        }}
        onNumberChange={(value) => {
          setPhoneNumber(value);
          setError(null);
          setSuccess(null);
        }}
        placeholder={t('customer.profile.phone.placeholder')}
        helperText={t('customer.profile.phone.helper')}
        disabled={saving}
        autoCompleteSection="customer-profile"
      />

      <CustomerPrimaryButton
        onClick={handleSave}
        disabled={saving || isUnchanged}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ mt: 2 }}
      >
        {saving ? t('common.saving') : t('common.save')}
      </CustomerPrimaryButton>
    </Box>
  );
};

export default CustomerProfilePhoneForm;
